import {
  VizScene,
  PointcloudDisplay,
  DataMessage,
  RangeRGBData,
  CameraOptions,
  CameraType,
} from '@ouster/webviz';
import { chakra, Flex, Box, Loader } from '@ouster/webviz-react-components';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Subject } from 'rxjs';

import { getOSFWorker } from '../getOSFWorker';
import { useStoreActions, useStoreState } from '../reducers';
import { selectPointCloudGlobalSettings } from '../reducers/vizScene';

const timeoutSeconds = 15;

export default function VisualTest(): JSX.Element {
  // 1) Signed URL and timeout (from query param)
  const signedUrlRef = useRef<string | null | undefined>(undefined);
  const [signedUrl, setSignedUrl] = useState<string | null | undefined>(
    undefined
  );

  const [initTimeoutSeconds] = useState<number>(10);

  // Read signed_url from query parameter and store in ref
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encodedUrl = params.get('signed_url');
    if (encodedUrl) {
      try {
        const decodedUrl = decodeURIComponent(encodedUrl);
        signedUrlRef.current = decodedUrl;
        setSignedUrl(decodedUrl);
      } catch (e) {
        signedUrlRef.current = null;
        setSignedUrl(null);
      }
    } else {
      signedUrlRef.current = null;
      setSignedUrl(null);
    }
  }, []);

  // 2) Redux store hooks for minimal pipeline + error reporting
  const { updateDataMessage, setHasVizError, setVizErrorMessage } =
    useStoreActions((a) => a.vizScene);
  const pointCloudObservable = useStoreState(
    (s) => s.vizScene.pointCloudObservable
  );
  const pointCloudGlobalSettings = useStoreState((s) =>
    selectPointCloudGlobalSettings(s.vizScene)
  );
  const hasVizError = useStoreState((s) => s.vizScene.hasVizError);
  const vizErrorMessage = useStoreState((s) => s.vizScene.vizErrorMessage);
  const dataMessageRef = useRef<Subject<DataMessage[]> | null>(null);

  // Validate the signed URL to avoid passing osf_info.json to the worker
  useEffect(() => {
    if (!signedUrl) return;
    const lower = signedUrl.toLowerCase();
    const isInfo = lower.includes('osf_info.json');
    const looksOsf = lower.includes('.osf');
    if (isInfo && !looksOsf) {
      setHasVizError(true);
      setVizErrorMessage(
        'Invalid signed_url: pass a signed OSF (.osf) URL, not osf_info.json'
      );
    }
  }, [signedUrl, setHasVizError, setVizErrorMessage]);

  // 3) Canvas + scene
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayDivRef = useRef<HTMLDivElement>(null);
  const [vizScene, setVizScene] = useState<VizScene | null>(null);
  const [pcDisplay, setPcDisplay] = useState<PointcloudDisplay | null>(null);
  const [isDrawn, setIsDrawn] = useState(false);
  const [showLoadingTimeout, setShowLoadingTimeout] = useState(false);

  // 4) Minimal paused setup: init scene once
  useEffect(() => {
    if (!signedUrl || vizScene || !canvasRef.current || !overlayDivRef.current)
      return;
    try {
      const DEFAULT_CAMERA_OPTIONS: CameraOptions = {
        cameraType: CameraType.PERSP,
        position: [20, 20, 10],
      };
      const scene = new VizScene(
        canvasRef.current,
        DEFAULT_CAMERA_OPTIONS,
        overlayDivRef.current
      );
      const pc = new PointcloudDisplay(
        scene,
        pointCloudGlobalSettings.coloringMode
      );
      setVizScene(scene);
      setPcDisplay(pc);
    } catch (e) {
      setHasVizError(true);
      setVizErrorMessage(
        e instanceof Error ? e.message : 'Failed to initialize VizScene'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    signedUrl,
    pointCloudGlobalSettings.coloringMode,
    setHasVizError,
    setVizErrorMessage,
  ]);

  // 5) Data pipeline: create subject once for this page
  useEffect(() => {
    const subj = new Subject<DataMessage[]>();
    dataMessageRef.current = subj;
    updateDataMessage(subj);
  }, [updateDataMessage]);

  // 6) Subscribe to point cloud observable -> draw once
  useEffect(() => {
    if (!pcDisplay || !vizScene || !pointCloudObservable) return () => {};
    const sub = pointCloudObservable.subscribe((packet: RangeRGBData[][]) => {
      try {
        if (!packet || packet.length === 0) return;
        pcDisplay.pointSize = pointCloudGlobalSettings.pointSize;
        pcDisplay.channelField = pointCloudGlobalSettings.coloringMode;
        pcDisplay.draw(packet);
        vizScene.render();
        if (!isDrawn) setIsDrawn(true);
        // paused => do not keep render loop running
        vizScene.cancelRenderLoop();
      } catch (e) {
        setHasVizError(true);
        setVizErrorMessage(
          e instanceof Error ? e.message : 'Failed to render point cloud'
        );
      }
    });
    return () => {
      sub.unsubscribe();
    };
  }, [
    pcDisplay,
    vizScene,
    pointCloudObservable,
    pointCloudGlobalSettings,
    isDrawn,
    setHasVizError,
    setVizErrorMessage,
  ]);

  // 7) Worker: open once with signed_url and fetch frame 0 only
  const worker = useMemo(() => getOSFWorker(), []);
  const openedRef = useRef(false);
  const initTimerRef = useRef<number | null>(null);
  const initReceivedRef = useRef(false);

  useEffect(() => {
    if (!signedUrlRef.current) return () => {};
    if (hasVizError) {
      // Invalid URL or other gating error already set; skip worker
      return () => {};
    }
    if (!window.Worker) {
      setHasVizError(true);
      setVizErrorMessage('Web Workers not supported');
      return () => {};
    }

    // Surface worker load errors
    worker.onerror = (ev) => {
      setHasVizError(true);
      setVizErrorMessage(
        `OSF worker failed to load: ${String(ev?.message ?? 'unknown error')}`
      );
    };

    worker.onmessage = (
      e: MessageEvent<
        | { frameIdx: number; dataMessages?: DataMessage[]; ts?: number }
        | { loadedFrames: number[] }
        | { framesCount: number; calculatedFps: number }
        | { command: 'init' | 'error'; message?: string }
      >
    ) => {
      if (!e.data) return;
      if ('command' in e.data) {
        const cmd = (e.data as { command: 'init' | 'error'; message?: string })
          .command;
        if (cmd === 'init') {
          initReceivedRef.current = true;
          if (initTimerRef.current) {
            window.clearTimeout(initTimerRef.current);
            initTimerRef.current = null;
          }
          // After init, request only the first frame
          try {
            worker.postMessage({ command: 'get', index: 0, preload: false });
          } catch (err) {
            setHasVizError(true);
            setVizErrorMessage('Failed to request frame from worker');
          }
        } else if (cmd === 'error') {
          setHasVizError(true);
          setVizErrorMessage(
            (e.data as { message?: string }).message ??
              'Worker reported an error'
          );
          return;
        }
      }
      if ('frameIdx' in e.data) {
        const msg = e.data as {
          frameIdx: number;
          dataMessages?: DataMessage[];
        };
        if (msg.dataMessages && msg.dataMessages.length > 0) {
          try {
            // push raw messages into pipeline via ref to avoid effect deps churn
            dataMessageRef.current?.next(msg.dataMessages);
          } catch (err) {
            setHasVizError(true);
            setVizErrorMessage('Failed to push data messages to pipeline');
          }
        } else {
          // No data for frame 0
          setHasVizError(true);
          setVizErrorMessage('No data messages received for frame 0');
        }
      }
    };

    if (!openedRef.current) {
      try {
        worker.postMessage({
          command: 'open',
          signedOsfUrl: signedUrlRef.current,
          bufferSize: 64,
          rate: 1,
          recordingId: 'visual-test',
        });
        openedRef.current = true;
        // Expect an 'init' message; set a timeout in case worker stalls
        if (initTimerRef.current) {
          window.clearTimeout(initTimerRef.current);
        }
        initReceivedRef.current = false;
        initTimerRef.current = window.setTimeout(() => {
          if (!initReceivedRef.current) {
            setHasVizError(true);
            setVizErrorMessage(
              `Worker initialization timed out after ${initTimeoutSeconds}s`
            );
          }
        }, Math.max(1, initTimeoutSeconds) * 1000);
      } catch (e) {
        setHasVizError(true);
        setVizErrorMessage('Failed to open OSF worker');
        return () => {};
      }
    }

    // Do not request a frame until we receive 'init' from worker

    return () => {
      worker.terminate();
      // Allow re-open if effect runs again
      openedRef.current = false;
      if (initTimerRef.current) {
        window.clearTimeout(initTimerRef.current);
        initTimerRef.current = null;
      }
    };
  }, [
    worker,
    setHasVizError,
    setVizErrorMessage,
    initTimeoutSeconds,
    hasVizError,
  ]);

  // 7.5) Optional timeout banner if stuck loading too long
  useEffect(() => {
    if (!signedUrl || hasVizError || isDrawn) return () => {};
    setShowLoadingTimeout(false);
    const ms = Math.max(1, timeoutSeconds) * 1000;
    const handle = window.setTimeout(() => {
      setShowLoadingTimeout(true);
    }, ms);
    return () => {
      window.clearTimeout(handle);
    };
  }, [signedUrl, hasVizError, isDrawn]);

  // 8) Render: loading, error, or canvas
  if (signedUrl === undefined) {
    return (
      <Flex
        w="100%"
        h="100vh"
        alignItems="center"
        justifyContent="center"
        data-testid="visualtest-loading"
      >
        <Loader label="Loading visual test" />
      </Flex>
    );
  }

  if (signedUrl === null) {
    return (
      <Box p="4" color="content-main" data-testid="visual-test-error">
        We need a signed url to start the visual test.
      </Box>
    );
  }

  if (hasVizError) {
    return (
      <Box p="6">
        <div data-testid="visual-test-error">
          We encountered an issue while trying to visualize your recording. The
          error message is as follows:{' '}
          <em id="errorMessageViz">{vizErrorMessage}</em>
        </div>
      </Box>
    );
  }

  return (
    <Box w="100%" h="100vh" position="relative" bg="#22272d" color="#ffffff">
      {!isDrawn && (
        <Flex
          w="100%"
          h="100%"
          alignItems="center"
          justifyContent="center"
          data-testid="visual-test-lidar-loading"
          position="absolute"
          inset={0}
          background="black"
          zIndex={1000}
        >
          <Loader label="Loading lidar viewer" />
        </Flex>
      )}
      {showLoadingTimeout && !isDrawn && !hasVizError && (
        <Box
          position="absolute"
          top="1rem"
          left="50%"
          transform="translateX(-50%)"
          px="4"
          py="2"
          bg="background-surface-strong"
          borderRadius="md"
          border="1px solid var(--border-color, #3a4149)"
          data-testid="visual-test-error"
        >
          Viewer did not draw within {timeoutSeconds}s.
        </Box>
      )}
      <Box
        w="100%"
        h="100%"
        position="absolute"
        ref={overlayDivRef}
        pointerEvents="none"
      />
      <chakra.canvas ref={canvasRef} w="100%" h="100%" />
    </Box>
  );
}
